'use client';

import { appVersion } from '@/config/app';
import { inter500, inter600 } from '@/config/fonts';
import LogoutButton from '@/shared/components/LogoutButton/LogoutButton';
import useAuth from '@/shared/hooks/useAuth';
import useUser from '@/shared/hooks/useUser';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AccountSelector from './AccountSelector/AccountSelector';
import Balance from './Balance/Balance';
import { useDrawerMenu } from './config';
import Stats from './Stats/Stats';
import ThemeSwitcher from './ThemeSwitcher';

const AppDrawer = () => {
  const { isLoggedIn } = useUser();
  const { handleLogout } = useAuth();
  const pathname = usePathname();
  const drawerMenu = useDrawerMenu();

  if (!isLoggedIn) {
    return null;
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        <AccountSelector />
        <Balance />
        <Stats />

        <div className="app-links">
          {drawerMenu.map((menuItem, idx) => {
            const isGroup = !!menuItem.groupTitle;

            if (isGroup) {
              return (
                <div key={idx}>
                  <li
                    className={
                      'mt-4 mb-2 flex h-6 items-center gap-3 px-3 text-gray-500'
                    }>
                    <span
                      className={classNames('text-xs text-gray-500', inter600.className)}>
                      {menuItem.groupTitle}
                    </span>
                  </li>
                  {menuItem.groupTitle === 'Support & Settings' && <ThemeSwitcher />}
                </div>
              );
            }

            const item = menuItem as Exclude<typeof menuItem, { groupTitle: string }>;
            const isActive = item.link === pathname && pathname !== '/';
            const hasOnClick = !!item.onClick;

            return hasOnClick ? (
              <li key={idx} className={'list-none'}>
                <button
                  onClick={item.onClick}
                  className={classNames(
                    'text-gray-default hover:!text-black-600 hover:bg-base-100 flex h-9 w-full items-center gap-3 rounded-lg px-3 dark:text-gray-500 dark:hover:!text-white',
                    {
                      '!text-black-600 bg-base-100 dark:!text-white': isActive,
                      'text-gray-500 dark:text-gray-400': !isActive,
                    }
                  )}>
                  <span>{item.icon}</span>
                  <span className={classNames(inter500.className, 'text-sm')}>
                    {item.title}
                  </span>
                  <span className="flex-1" />
                  {!!item.badge && (
                    <div className="badge badge-primary">{item.badge}</div>
                  )}
                </button>
              </li>
            ) : (
              <li key={idx} className={'list-none'}>
                <Link
                  href={item.link ?? '/'}
                  target={item.target}
                  className={classNames(
                    'text-gray-default hover:!text-black-600 hover:bg-base-100 flex h-9 items-center gap-3 rounded-lg px-3 dark:text-gray-500 dark:hover:!text-white',
                    {
                      '!text-black-600 bg-base-100 dark:!text-white': isActive,
                      'text-gray-500 dark:text-gray-400': !isActive,
                    }
                  )}>
                  <span>{item.icon}</span>
                  <span className={classNames(inter500.className, 'text-sm')}>
                    {item.title}
                  </span>
                  <span className="flex-1" />
                  {!!item.badge && (
                    <div className="badge badge-primary">{item.badge}</div>
                  )}
                </Link>
              </li>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="text-gray-default mt-auto mb-2 flex w-full items-center justify-between">
        {!!isLoggedIn && (
          <LogoutButton
            // className="text-gray-default hover:!text-black-600 flex h-9 items-center gap-3 rounded-lg px-3 hover:!bg-gray-200"
            onLogout={handleLogout}
          />
        )}

        <div className="px-2">
          <span className={classNames(inter500.className, 'text-xs')}>{appVersion}</span>
        </div>
      </div>
    </>
  );
};

export default AppDrawer;
